import {
    createCart,
    getCartByToken,
    getMyCart,
    gtmCartPageBasketOverview,
    localStorage,
    sessionController,
} from "@jog/shared"
import type { JoggCart } from "@jog/types"
import { useSession } from "next-auth/react"
import { useRouter } from "next/router"
import { createContext, useCallback, useContext, useEffect, useState } from "react"

import { usePending } from "../components"
import { CartStore } from "../stores"

export const useCart = ({ cartStore }: { cartStore: CartStore }) => {
    const router = useRouter()
    const { setPending } = usePending()
    useEffect(() => {
        if (cartStore.isCartViewLoaded) gtmCartPageBasketOverview(cartStore.cartView?.body?.lineItems || [])
    }, [cartStore.cartView, cartStore.isCartViewLoaded])
    const init = useCallback(
        async (recovery: string) => {
            if (recovery) {
                await getCartByToken(recovery)
                    .then((cart) => {
                        if (cart.body) {
                            cartStore.syncCart(cart.body)
                            window.history.pushState({ ...window.history.state, as: "/cart" }, "", "/cart")
                        }
                    })
                    .catch(() => {})
            }
            await cartStore.cleanCosts()
            if (cartStore.cartView?.body?.cartMessage?.length) await cartStore.validateCart()
        },
        [cartStore]
    )

    useEffect(() => {
        if (router.isReady) {
            const recoveryToken = router.query.recovery as string
            setPending(true)
            init(recoveryToken).finally(() => {
                setPending(false)
            })
        }
    }, [init, router.isReady, router.query.recovery, setPending])
}

interface CartContextState {
    cart: JoggCart | null
    isLoading: boolean
}

export const CartContext = createContext<CartContextState>({
    cart: null,
    isLoading: false,
})

export const CartProvider = ({ children, cartStore }: { children: React.ReactNode; cartStore: CartStore }) => {
    const [cart, _setCart] = useState<JoggCart | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const setCart = useCallback((cart: JoggCart) => {
        _setCart(cart)
        localStorage.setItem("cart", cart)
    }, [])
    useEffect(() => {
        if (cartStore.cartView) {
            setCart(cartStore.cartView.body)
        }
    }, [cartStore.cartView, setCart])

    const router = useRouter()
    const session = useSession()

    useEffect(() => {
        if (session.data?.user) {
            sessionController().set(session.data)
        }
    }, [session.data])

    useEffect(() => {
        if (router.isReady && !isLoading) {
            if (!session.data?.user) return
            if (!cart) {
                setIsLoading(true)
                getMyCart()
                    .then(async (cart) => {
                        if (cart.body) {
                            setCart(cart.body)
                            cartStore.syncCart(cart.body)
                            return
                        }
                        const newCart = await createCart()
                        setCart(newCart)
                        cartStore.syncCart(newCart)
                    })
                    .finally(() => {
                        setIsLoading(false)
                    })
            }
        }
    }, [cart, cartStore, isLoading, router.isReady, session.data?.user, setCart])

    return <CartContext.Provider value={{ cart, isLoading }}>{children}</CartContext.Provider>
}

export const useCartContext = () => {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error("useCartContext must be used within a CartProvider")
    }
    return context
}
